import { Component, computed, inject, OnInit, ViewChild, HostListener } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { BadgeModule } from 'primeng/badge';
import { Tooltip } from 'primeng/tooltip';
import { ImpersonationStore } from 'shared';

import { LanguageToggleComponent } from '../../components/language-toggle/language-toggle.component';
import { PendingInvitationsStore } from '../../core/stores/pending-invitations.store';
import { AuthStore } from '../../features/authentication/store/auth.store';
import { OnboardingStore } from '../../features/onboarding/store/onboarding.store';
import { ImpersonationDropdownComponent } from '../impersonation-dropdown/impersonation-dropdown.component';
import { SidebarStore } from '../sidebar.store';
import { TenantPickerComponent } from '../tenant-picker/tenant-picker.component';
import { UserMenuComponent } from '../user-menu/user-menu.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    RouterLink,
    UserMenuComponent,
    TenantPickerComponent,
    LanguageToggleComponent,
    ImpersonationDropdownComponent,
    Tooltip,
    BadgeModule,
  ],
  templateUrl: './header.component.html',
  styles: `
    :host {
      display: block;
    }
  `,
})
export class HeaderComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly pendingInvitationsStore = inject(PendingInvitationsStore);
  private readonly onboardingStore = inject(OnboardingStore);

  protected authStore = inject(AuthStore);
  protected sidebarStore = inject(SidebarStore);
  protected impersonationStore = inject(ImpersonationStore);

  /** Logo links to /onboarding/complete during onboarding, /dashboard otherwise */
  protected readonly logoLink = computed(() =>
    this.onboardingStore.isComplete() === false ? '/onboarding/complete' : '/dashboard',
  );

  protected readonly pendingInvitationsCount = computed(() => this.pendingInvitationsStore.count());

  @ViewChild('impersonationDropdown') impersonationDropdown!: ImpersonationDropdownComponent;

  protected isDropdownOpen = false;

  protected canImpersonate = computed(() => {
    const user = this.authStore.user();
    return user ? this.impersonationStore.canImpersonate(user) : false;
  });

  protected showImpersonationButton = computed(() => {
    return this.canImpersonate() && !this.impersonationStore.isImpersonating();
  });

  toggleImpersonationDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      // Focus input after dropdown opens
      setTimeout(() => {
        this.impersonationDropdown?.focusInput();
      }, 100);
    }
  }

  closeImpersonationDropdown(): void {
    this.isDropdownOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const dropdown = document.querySelector('.impersonation-container');
    if (dropdown && !dropdown.contains(target)) {
      this.isDropdownOpen = false;
    }
  }

  ngOnInit(): void {
    this.pendingInvitationsStore.load();
  }

  goToInvitations(): void {
    void this.router.navigate(['/profile'], { fragment: 'invitations' });
  }
}
